import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const SECTIONS = [
  {
    id: 'basics',
    icon: '🏏',
    color: '#f59e0b',
    title: 'How It Works',
    subtitle: 'The big picture',
    rules: [
      {
        q: 'What is IPLTrade?',
        a: 'IPLTrade is a free fantasy IPL stock market. You buy and sell virtual "stocks" in IPL players. When a player performs well in a live match, their stock price rises. When they underperform, it falls. Your goal is to build the highest-value portfolio by the end of the season.',
      },
      {
        q: 'Is real money involved?',
        a: 'No. Everything runs on virtual points. No real money is ever required, deposited, or won. It is purely a skill-based fantasy game.',
      },
      {
        q: 'When can I trade?',
        a: 'Trading is open at all times except during a live IPL match. The market locks automatically when a match starts and unlocks once it ends — this keeps prices fair for everyone.',
      },
    ],
  },
  {
    id: 'balance',
    icon: '⬡',
    color: '#10b981',
    title: 'Points & Balance',
    subtitle: 'Your virtual wallet',
    rules: [
      {
        q: 'How many points do I start with?',
        a: 'Every user starts with 2000 points. This is your starting cash — use it to buy player stocks.',
      },
      {
        q: 'Can my total portfolio value grow beyond 2000?',
        a: 'Yes! If you buy a player stock and that player performs well, their price rises — and so does your total portfolio value (cash + stock value). Great picks can push your total well above 2000. The 2000 cap only limits how much you can spend when buying — you cannot buy if your current total already reaches 2000, but price appreciation beyond that is your reward.',
      },
      {
        q: 'What happens to unused cash?',
        a: 'Cash sitting idle earns nothing. It is best deployed into player stocks that you expect to rise before a match. Idle cash is a missed opportunity.',
      },
    ],
  },
  {
    id: 'trading',
    icon: '🛒',
    color: '#3b82f6',
    title: 'Buying & Selling',
    subtitle: 'How trades work',
    rules: [
      {
        q: 'How do I buy a player stock?',
        a: 'Go to the Market page, find a player, and tap Buy. This adds a buy order to your Order Bucket. Once you are happy with all your orders, tap Execute to complete them in one go.',
      },
      {
        q: 'How do I sell?',
        a: 'Go to your Portfolio, select the player you hold, and tap Sell. This adds a sell order to your bucket. Execute when ready. You can only sell players you currently own.',
      },
      {
        q: 'What is the Order Bucket?',
        a: 'The Order Bucket lets you queue multiple buys and sells before committing. You can review the net cost, adjust orders, and execute everything at once — great for planning your full 2000-point budget in one shot.',
      },
      {
        q: 'Can I hold more than one unit of a player?',
        a: 'No. You can hold at most 1 unit of each player. This ensures everyone has equal access to top players.',
      },
      {
        q: 'What price do I pay?',
        a: 'You trade at the player\'s current stock price at the moment you execute your order. If the price changes between adding to the bucket and executing, the bucket will warn you.',
      },
    ],
  },
  {
    id: 'trade-limit',
    icon: '🔁',
    color: '#ef4444',
    title: 'Trade Limit',
    subtitle: '200 trades for the whole season',
    rules: [
      {
        q: 'How many trades do I get?',
        a: 'You have 200 total trades for the entire IPL season. Each buy counts as 1 trade, each sell counts as 1 trade. The counter is shown in the top navbar at all times.',
      },
      {
        q: 'Do trades reset?',
        a: 'No. Trade counts do not reset mid-season. They reset only at the start of a new IPL season. Plan your trades wisely — every buy and every sell costs one trade.',
      },
      {
        q: 'What happens when I run out?',
        a: 'Once your 200 trades are used, you cannot make any more buys or sells for the rest of the season. Your portfolio stays locked in its current state until the season resets.',
      },
    ],
  },
  {
    id: 'prices',
    icon: '📈',
    color: '#8b5cf6',
    title: 'Stock Prices',
    subtitle: 'How prices move',
    rules: [
      {
        q: 'What makes a price go up?',
        a: 'Live match performance drives prices. Batting: runs scored, strike rate above 120, fours (+), sixes (++), and milestone bonuses at 30, 50, 75, and 100 runs. Bowling: each wicket, economy below 7, and milestone bonuses for 3, 4, and 5-wicket hauls. Fielding: catches.',
      },
      {
        q: 'What makes a price go down?',
        a: 'Poor performance drops prices. Batting: a duck (0 runs off 3+ balls) is penalised. A slow strike rate below 120 reduces the price, and a very slow strike rate below 100 applies an additional penalty. Bowling: economy above 7 in 2+ overs is penalised. Not playing at all causes a small 0.5% daily decay.',
      },
      {
        q: 'How often do prices update?',
        a: 'In production mode, prices update every 5 minutes during a live match, with pre-match polling starting 30 minutes before start time. In staging mode, prices are simulated much faster. The app auto-refreshes data frequently, so you usually do not need manual page refresh.',
      },
      {
        q: 'What counts as the "day base" for % up/down?',
        a: 'Daily % change is calculated from the 2:30 PM IST base price. The active day session runs from 2:30 PM IST to 1:30 AM IST, and the player day chart is aligned to this same window.',
      },
      {
        q: 'Is there a price floor or ceiling?',
        a: 'Yes. A player\'s price can never drop below 60% of their base price, and cannot rise above 140% of their base price in a single match session. The absolute maximum price is 999 points.',
      },
      {
        q: 'What is daily decay?',
        a: 'Players whose teams are not playing that day lose 0.5% of their price at midnight. This simulates a "holding cost" and rewards active portfolio management. The floor is always 60% of base price.',
      },
    ],
  },
  {
    id: 'market-lock',
    icon: '🔒',
    color: '#f59e0b',
    title: 'Market Lock',
    subtitle: 'When trading pauses',
    rules: [
      {
        q: 'Why does the market lock?',
        a: 'The market locks during live matches to prevent anyone from trading after seeing live score updates. This keeps competition fair — everyone faces the same information conditions. Price calculation can still continue in the background while trading stays paused.',
      },
      {
        q: 'How do I know the market is locked?',
        a: 'A banner appears at the top of the app showing which match is live. All buy and sell buttons are disabled. The banner disappears once the match ends and trading reopens.',
      },
      {
        q: 'When does it unlock?',
        a: 'The market unlocks automatically when the match ends and final prices have been calculated. This typically happens within a few minutes of the last ball.',
      },
    ],
  },
  {
    id: 'contests',
    icon: '⚔️',
    color: '#ec4899',
    title: 'Contests',
    subtitle: 'Compete with friends',
    rules: [
      {
        q: 'What is a contest?',
        a: 'A contest is a private mini-game between a group of friends or colleagues. Each contest has its own separate portfolio (separate from your main one), its own leaderboard, and can have custom entry rules.',
      },
      {
        q: 'Does a contest use my main balance?',
        a: 'No. Each contest gives you a fresh 1000-point budget that is completely separate from your main portfolio. Losing in a contest does not affect your global ranking.',
      },
      {
        q: 'How do I invite people?',
        a: 'When you create a contest, you get a unique invite code or link. Share it with friends. Anyone who joins with the code enters the same contest leaderboard.',
      },
    ],
  },
  {
    id: 'leaderboard',
    icon: '🏆',
    color: '#fbbf24',
    title: 'Leaderboard & Badges',
    subtitle: 'Global rankings',
    rules: [
      {
        q: 'How is the leaderboard calculated?',
        a: 'Your global rank is based on total portfolio value: cash points + current market value of all holdings. Rankings update after every price change.',
      },
      {
        q: 'What are the badges?',
        a: '🥇 Gold — Top 100 players. 🥈 Silver — Rank 101 to 500. 🥉 Bronze — Rank 501 to 1000. Badges are live and update as rankings shift throughout the season.',
      },
      {
        q: 'Is there a season reset?',
        a: 'Yes. At the end of each IPL season, all portfolios and trade counts reset for a fresh start. Final rankings are preserved in the Past Seasons history tab on the leaderboard.',
      },
    ],
  },
];

function RuleCard({ rule }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      onClick={() => setOpen(o => !o)}
      style={{
        borderRadius: 10,
        border: '1px solid var(--border)',
        background: open ? 'var(--bg-elevated)' : 'transparent',
        marginBottom: 6,
        cursor: 'pointer',
        transition: 'background 0.15s',
        overflow: 'hidden',
      }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px', gap: 12,
      }}>
        <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', flex: 1 }}>
          {rule.q}
        </span>
        {open
          ? <ChevronUp size={15} color="var(--text-muted)" style={{ flexShrink: 0 }} />
          : <ChevronDown size={15} color="var(--text-muted)" style={{ flexShrink: 0 }} />
        }
      </div>
      {open && (
        <div style={{
          padding: '0 16px 14px',
          fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.7,
          borderTop: '1px solid var(--border)',
          paddingTop: 12,
        }}>
          {rule.a}
        </div>
      )}
    </div>
  );
}

export default function RulesPage() {
  const [activeSection, setActiveSection] = useState(null);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', paddingBottom: '3rem' }}>

      {/* Hero */}
      <div className="page-header" style={{ textAlign: 'center', paddingBottom: '1.5rem' }}>
        <div style={{ fontSize: '2.8rem', marginBottom: 8 }}>📖</div>
        <h1 className="page-title" style={{ fontSize: '1.75rem' }}>Rule Book</h1>
        <p className="page-subtitle" style={{ maxWidth: 480, margin: '0 auto' }}>
          Everything you need to know to trade like a pro. Tap any question to expand.
        </p>
      </div>

      {/* Quick-nav pills */}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1.5rem',
        justifyContent: 'center',
      }}>
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => {
              setActiveSection(activeSection === s.id ? null : s.id);
              document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            style={{
              padding: '5px 14px', borderRadius: 20, border: '1px solid',
              borderColor: activeSection === s.id ? s.color : 'var(--border)',
              background: activeSection === s.id ? s.color + '18' : 'transparent',
              color: activeSection === s.id ? s.color : 'var(--text-muted)',
              fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}
          >
            {s.icon} {s.title}
          </button>
        ))}
      </div>

      {/* Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {SECTIONS.map(section => (
          <div
            key={section.id}
            id={`section-${section.id}`}
            className="card"
            style={{ padding: 0, overflow: 'hidden', scrollMarginTop: 80 }}
          >
            {/* Section header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '16px 20px',
              borderBottom: '1px solid var(--border)',
              background: `linear-gradient(90deg, ${section.color}0f 0%, transparent 100%)`,
              borderLeft: `3px solid ${section.color}`,
            }}>
              <span style={{
                fontSize: '1.4rem', width: 40, height: 40, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: section.color + '18', borderRadius: 10, flexShrink: 0,
              }}>{section.icon}</span>
              <div>
                <div style={{
                  fontFamily: 'var(--font-display)', fontWeight: 700,
                  fontSize: '1.05rem', color: 'var(--text-primary)',
                }}>
                  {section.title}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
                  {section.subtitle}
                </div>
              </div>
              <div style={{
                marginLeft: 'auto', background: section.color + '20',
                color: section.color, borderRadius: 20, padding: '2px 10px',
                fontSize: '0.68rem', fontWeight: 700, flexShrink: 0,
              }}>
                {section.rules.length} {section.rules.length === 1 ? 'rule' : 'rules'}
              </div>
            </div>

            {/* Rules list */}
            <div style={{ padding: '12px 16px' }}>
              {section.rules.map((rule, i) => (
                <RuleCard key={i} rule={rule} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div style={{
        marginTop: '1.5rem', padding: '16px 20px',
        background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)',
        borderRadius: 12, textAlign: 'center',
      }}>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.7, margin: 0 }}>
          IPLTrade is a free, skill-based fantasy game — no real money involved.
          Rules may be updated at the start of each IPL season.{' '}
          <span style={{ color: 'var(--text-secondary)' }}>Have fun and trade responsibly! 🏏</span>
        </p>
      </div>

    </div>
  );
}
